import {
  type DiagramDatabase,
  type DiagramProperties,
  type DiagramNode,
  type DiagramEdge,
} from '../parsing/DiagramStructure';
import {
  type WhiteboardData,
  type LayoutOptions,
  type PropertyDefinitions,
} from '../types';
import type ColorScheme from '../colors/ColorScheme';
import { DefaultColorScheme } from '../colors/ColorScheme';
import { colorSchemes } from '../colors';
import DiagramError from './DiagramError';
import { renderWhiteboardToSvg } from './svgUtils';
import DiagramGenerator from './DiagramGenerator';
import properties from './properties';
import { type CstNodeLocation } from 'chevrotain';

// Local definition of label params to avoid circular dependency with @aha-app/whiteboard import
interface HasLabel {
  labelPosition?: string;
  label?: string;
}

export type RenderToWhiteboardOptions = {
  nodeAttrs: (node: DiagramNode) => Partial<fabric.Object>;
  edgeAttrs: (
    node: DiagramNode,
    child: DiagramNode
  ) => Partial<fabric.Object & HasLabel>;
};

// Base class for all diagrams. Diagrams must implement the key methods of this class.
class Diagram {
  database: DiagramDatabase;

  // Human readable name of the diagram.
  static diagramName = 'Generic';
  // Unique identifier for the diagram type used in diagram text.
  static diagramType = 'generic';

  // Description of the diagram to use in LLM documentation.
  static description = 'A generic diagram type that can be extended.';

  // DSL describing the available properties specific to this diagram.
  static diagramProperties: PropertyDefinitions = properties;
  // DSL describing the properties for this diagram, including inherited properties.
  static allowedProperties: PropertyDefinitions = this.diagramProperties;

  constructor(database: DiagramDatabase) {
    this.database = database;
    this.validateAllProperties();
  }

  // Layout the diagram. Implement in sub-classes to provide specific layout logic.
  async layout(_layoutOptions?: LayoutOptions) {
    throw new Error('layout method not implemented');
  }

  width() {
    return this.database.layout.width;
  }

  height() {
    return this.database.layout.height;
  }

  // Whether the diagram completely failed to parse.
  isError() {
    return false;
  }

  renderToWhiteboard(_options?: RenderToWhiteboardOptions): any {
    throw new Error('renderToWhiteboard method not implemented');
  }

  renderToSvg(): string {
    const whiteboard = this.renderToWhiteboard();
    return renderWhiteboardToSvg(whiteboard);
  }

  async toWhiteboard(layoutOptions?: LayoutOptions): Promise<WhiteboardData> {
    await this.fullLayout(layoutOptions);
    return this.renderToWhiteboard();
  }

  async toSvg(layoutOptions?: LayoutOptions) {
    await this.fullLayout(layoutOptions);
    return this.renderToSvg();
  }

  // Internal method to layout all nested diagrams depth first.
  async fullLayout(layoutOptions?: LayoutOptions) {
    // Layout any nested diagrams first.
    for (const node of this.database.nodes()) {
      if (node.database) {
        node.subDiagram = DiagramGenerator.diagramFromDatabase(node.database);
        if (node.subDiagram) {
          // Layout the sub-diagram first.
          await node.subDiagram.fullLayout(layoutOptions);
          node.subDiagram.layout = node.subDiagram.database.layout;
        }
      }
    }
    // Layout the current diagram.
    await this.layout(layoutOptions);

    // Compute the bounding box of the diagram.
    this.computeBoundingBox();
  }

  // Compute the bounding box of the diagram.
  computeBoundingBox() {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const node of this.database.nodes()) {
      minX = Math.min(minX, node.layout.left);
      minY = Math.min(minY, node.layout.top);
      maxX = Math.max(maxX, node.layout.left + node.layout.width);
      maxY = Math.max(maxY, node.layout.top + node.layout.height);
    }

    this.database.layout.left = Math.min(0, minX);
    this.database.layout.top = Math.min(0, minY);
    let width = 250;
    if (isFinite(maxX - minX)) width = maxX - minX;
    this.database.layout.width = width;
    let height = 250;
    if (isFinite(maxY - minY)) height = maxY - minY;
    this.database.layout.height = height;
  }

  // Compute the size of a node based on its properties and title.
  async sizeNode(node: DiagramNode, layoutOptions?: LayoutOptions) {
    if (node.database) {
      node.layout.width = node.database.layout.width;
      node.layout.height = node.database.layout.height;

      return;
    }

    // Default node size.
    node.layout.width ??= this.getNodeProperty(node, 'nodeWidth') as number;
    node.layout.height ??= this.getNodeProperty(node, 'nodeHeight') as number;

    // Make the nodes bigger if necessary to fit the text.
    if (layoutOptions?.measureTextFunc && node.title && node.title.length > 0) {
      // We iterate here to get a pleasing ratio of width to height.
      while (true) {
        node.layout.height = this.getNodeProperty(node, 'nodeHeight') as number;

        const { width, height } = await layoutOptions.measureTextFunc(
          node.title,
          {
            containerWidth: node.layout.width,
          }
        );

        node.layout.width = Math.max(node.layout.width, width);
        node.layout.height = Math.max(node.layout.height, height);

        if (node.layout.height > node.layout.width) {
          node.layout.width = node.layout.width * 1.5;
        } else {
          break;
        }
      }
    }
  }

  // Validate that all of the properties for the diagram are valid.
  validateAllProperties() {
    this.validateProperties(this.database.properties);
    for (const node of this.database.nodes()) {
      this.validateProperties(node.properties);
    }
    for (const edge of this.database.edges()) {
      this.validateProperties(edge.properties);
    }
  }

  validateProperties(properties: DiagramProperties) {
    const diagramAllowedProperties = (this.constructor as typeof Diagram)
      .allowedProperties;

    for (const name in properties) {
      if (!diagramAllowedProperties[name]) {
        this.addError(
          `Unknown property: '${name}'`,
          properties[name].keyLocation
        );
      }
    }
  }

  getNodeProperty(
    node: DiagramNode | DiagramDatabase,
    name: string
  ): string | number {
    return (
      this.getProperty(node.properties, name, false) ||
      this.getDiagramProperty(name)
    );
  }

  getEdgeProperty(edge: DiagramEdge, name: string): string | number {
    return (
      this.getProperty(edge.properties, name, false) ||
      this.getDiagramProperty(name)
    );
  }

  getDiagramProperty(name: string): string | number | any {
    return this.getProperty(this.database.properties, name, true)!;
  }

  // Get the value of a property through a fallback mechanism:
  // - If the property is set explicitly use that.
  // - If the property is not set, try to use the variant's value.
  // - If the property is not set in the variant, use the default value.
  private getProperty(
    properties: DiagramProperties,
    name: string,
    withDefault = true
  ): string | number | null {
    const diagramAllowedProperties = (this.constructor as typeof Diagram)
      .allowedProperties;

    // Validate that this is an known property. This is a development time
    // error and should not happen in production.
    const propertyDefinition = diagramAllowedProperties[name];
    if (!propertyDefinition) {
      throw new Error(`Unhandled property: ${name}`);
    }

    let value = properties[name]?.value;

    // If we don't have a value, try the variant and see if it specifies a
    // value for us.
    if (value === undefined && name !== 'variant' && withDefault) {
      const variantProperties = this.getDiagramProperty('variant');
      if (variantProperties) {
        value = variantProperties[name];
      }
    }

    // Validate that the value is allowed.
    if (value !== undefined) {
      switch (propertyDefinition.type) {
        case 'select':
          for (const allowedValue of propertyDefinition.options) {
            if (value === allowedValue.value) {
              return value;
            }
          }
          this.addError(
            `'${value}' is not an allowed value for ${name}`,
            properties[name]?.valueLocation
          );
          break;

        case 'number': {
          const numericValue = Number(value);

          if (
            numericValue < propertyDefinition.rangeFrom ||
            numericValue > propertyDefinition.rangeTo
          ) {
            this.addError(
              `${numericValue} is outside the allowed range of ${propertyDefinition.rangeFrom} to ${propertyDefinition.rangeTo}`,
              properties[name].valueLocation
            );
          } else {
            return numericValue;
          }
          break;
        }

        case 'selectProperties':
          for (const allowedValue of propertyDefinition.options) {
            if (value === allowedValue.value) {
              return allowedValue.properties;
            }
          }
          this.addError(
            `'${value}' is not an allowed value for ${name}`,
            properties[name]?.valueLocation
          );
          break;

        default: {
          const check: never = propertyDefinition;
          throw new Error(`Unknown property: ${check}`);
        }
      }
    }

    if (withDefault) {
      // Fall back to the default value.

      switch (propertyDefinition.type) {
        case 'select':
          return propertyDefinition.options[0].value;

        case 'number':
          return propertyDefinition.default ?? null;

        case 'selectProperties':
          return propertyDefinition.options[0]?.properties;

        default: {
          const check: never = propertyDefinition;
          throw new Error(`Unknown property: ${check}`);
        }
      }
    }

    return null;
  }

  // Get the color scheme.
  getColorScheme(): typeof ColorScheme {
    const colorSchemeName = this.getDiagramProperty('colorScheme');

    for (const colorScheme of colorSchemes) {
      if (colorScheme.value === colorSchemeName) {
        return colorScheme;
      }
    }

    return DefaultColorScheme;
  }

  // Given the ID of a node or edge, return the start and end text offsets
  // for that item.
  getTextSelectionById(id: string): CstNodeLocation | null {
    const item = this.database.nodesById[id] || this.database.edgesById[id];
    if (item && item.location) {
      return item.location;
    } else {
      return null;
    }
  }

  errors: DiagramError[] = [];

  addError(message: string, location?: CstNodeLocation) {
    this.addErrorObject(new DiagramError(message, location));
  }

  // Record an error message against this diagram. We only keep unique
  // errors.
  addErrorObject(error: DiagramError) {
    if (this.errors.some(e => e.equals(error))) {
      return;
    }
    this.errors.push(error);
  }

  // Clear any errors.
  clearErrors() {
    this.errors = [];
  }
}

export default Diagram;
